import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import _ from 'lodash';
import AppContext from '../AppContext';
import { ProgressBar } from '../Components';
import { getProductsImages } from '../selectors';
import {
  cacheImages,
  clearImageCacheRefresh,
  getImageUrlsToCache,
  isImageCacheRefreshPending,
} from '../utils/imageCache';
import { getTranslateByScope } from '../i18n';
import styles from './ImageCache.module.scss';

const translate = getTranslateByScope('app.imageCache');

// Only show the progress when it takes a while - everything cached is near instant
const SHOW_PROGRESS_AFTER = 1000;

// Caches all product images once the products are loaded (see utils/imageCache)
export const ImageCache = () => {
  const context = useContext(AppContext);
  const products = _.get(context, 'entities.products');
  const urls = useMemo(
    () => (_.isEmpty(products) ? [] : getImageUrlsToCache(getProductsImages(context))),
    // only recompute when the products change, not on every context update
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products],
  );

  const [progress, setProgress] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!urls.length) return undefined;

    let cancelled = false;
    const refresh = isImageCacheRefreshPending();
    const showTimeout = setTimeout(() => setIsVisible(true), SHOW_PROGRESS_AFTER);

    let lastPercent = -1;
    cacheImages(urls, {
      refresh,
      isCancelled: () => cancelled,
      onProgress: (done, total) => {
        const percent = Math.floor((done / total) * 100);
        if (percent === lastPercent) return;
        lastPercent = percent;
        setProgress({ done, total, percent });
      },
    }).then(({ failed }) => {
      if (cancelled) return;
      // Keep the request when offline so the images are replaced next time
      if (refresh && !failed) clearImageCacheRefresh();
      clearTimeout(showTimeout);
      setIsVisible(false);
      setProgress(null);
    });

    return () => {
      cancelled = true;
      clearTimeout(showTimeout);
    };
  }, [urls]);

  if (!isVisible || !progress) return null;

  return (
    <div className={styles.imageCache} role="status">
      <div className={styles.label}>
        {translate('progress', { done: progress.done, total: progress.total })}
      </div>
      <ProgressBar value={progress.percent} />
    </div>
  );
};

export default ImageCache;
